import { useEffect, useState, useRef } from 'react';
import { useSubscription, gql } from 'urql';
import { DocumentExtractionStatus } from '@wove/common';
import { useQueryClient } from '@tanstack/react-query';
const DOCUMENT_STATUS_SUBSCRIPTION = gql `
  subscription DocumentStatusUpdate($shipmentId: String!, $documentId: String) {
    documentStatusUpdate(shipmentId: $shipmentId, documentId: $documentId) {
      documentId
      shipmentId
      status
      error
      timestamp
      extractedData
    }
  }
`;
export function useDocumentStatusSubscription(shipmentId, initialDocuments) {
    const [documentStatuses, setDocumentStatuses] = useState({});
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [reconnectCount, setReconnectCount] = useState(0);
    const hasConnectedOnce = useRef(false);
    const queryClient = useQueryClient();
    // Initialize with existing documents
    useEffect(() => {
        if (initialDocuments && initialDocuments.length > 0) {
            setDocumentStatuses(prev => {
                const updated = { ...prev };
                initialDocuments.forEach(doc => {
                    // Only initialize if not already in state
                    if (!updated[doc.id]) {
                        updated[doc.id] = {
                            id: doc.id,
                            extractionStatus: doc.extractionStatus,
                        };
                    }
                });
                return updated;
            });
        }
    }, [initialDocuments]);
    const [{ data, fetching: loading, error, stale }] = useSubscription({
        query: DOCUMENT_STATUS_SUBSCRIPTION,
        variables: { shipmentId: shipmentId || '', documentId: undefined },
        pause: !shipmentId,
    });
    // Debug logging
    useEffect(() => {
        console.log('[DocumentStatusSubscription] State:', {
            loading,
            error: error?.message,
            hasData: !!data,
            stale,
            shipmentId,
        });
    }, [loading, error, data, stale, shipmentId]);
    // Track connection status
    useEffect(() => {
        if (!shipmentId) {
            setConnectionStatus('disconnected');
            hasConnectedOnce.current = false;
            return;
        }
        if (error) {
            setConnectionStatus('disconnected');
            if (hasConnectedOnce.current) {
                setReconnectCount(prev => prev + 1);
            }
            console.error('[DocumentStatusSubscription] Subscription error:', error);
        }
        else if (!hasConnectedOnce.current) {
            // First connection - consider it connected after a brief moment
            // This handles the case where URQL keeps loading=true for subscriptions
            const timer = setTimeout(() => {
                if (!error) {
                    setConnectionStatus('connected');
                    hasConnectedOnce.current = true;
                    console.log('[DocumentStatusSubscription] Connected to subscription');
                }
            }, 1000); // Give it 1 second to establish connection
            return () => clearTimeout(timer);
        }
        else {
            // Already connected once, so if no error, we're connected
            setConnectionStatus('connected');
        }
    }, [error, shipmentId]);
    // Update document statuses when subscription data changes
    useEffect(() => {
        console.log('data update.....', data);
        if (data?.documentStatusUpdate) {
            const update = data.documentStatusUpdate;
            setDocumentStatuses(prev => ({
                ...prev,
                [update.documentId]: {
                    ...prev[update.documentId],
                    id: update.documentId,
                    extractionStatus: update.status,
                    extractionError: update.error || undefined,
                    extractedAt: update.status === DocumentExtractionStatus.COMPLETED ? update.timestamp : undefined,
                },
            }));
            // Invalidate the documents query when extraction completes to fetch the extracted data
            if (update.status === DocumentExtractionStatus.COMPLETED && shipmentId) {
                console.log('[DocumentStatusSubscription] Extraction completed, invalidating documents query');
                // Try both possible query keys
                queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId, 'documents'] });
                queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] });
            }
        }
    }, [data, shipmentId, queryClient]);
    return {
        documentStatuses,
        loading,
        error,
        latestUpdate: data?.documentStatusUpdate,
        connectionStatus,
        reconnectCount,
        isConnected: connectionStatus === 'connected',
        isReconnecting: connectionStatus === 'connecting' && reconnectCount > 0,
    };
}
